// material
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
  Box,
  Button,
  TextField,
  Autocomplete,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Pagination,
} from '@mui/material';
import { omitBy } from 'lodash';
import { useMemo, useState } from 'react';
import FileDownload from 'js-file-download';

// components
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import { UserListHead } from '../sections/@dashboard/user';

// store
import { api as stockApi, hooks as stockHooks } from '../store/domains/stock';
import Axios from '../services';

const TABLE_HEAD = [
  { id: 'hubId', label: 'Hub ID', alignRight: false },
  { id: 'hubName', label: 'Hub Name', alignRight: false },
  { id: 'identifier', label: 'Identifier', alignRight: false },
  { id: 'partnerId', label: 'Partner ID', alignRight: true },
  { id: 'partnerName', label: 'Partner Name', alignRight: true },
  { id: '' },
];

export default function History() {
  const [partnerValue, setPartnerValue] = useState('');
  const [hubValue, setHubValue] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [hubId, setHubId] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [hubInputValue, setHubInputValue] = useState('');
  const [type, setType] = useState(2);
  const [buttonDisplay, setButtonDisplay] = useState(false);

  const { handleChangeRowsPerPage, rowsPerPage, page, handleChangePage } = stockHooks.useStock();
  const { data: posManagerOrganizations } = hubId
    ? stockApi.useGetPosManagerOrganizations(hubId)
    : stockApi.useGetOrganizations(null);

  const { data: hubList } = stockApi.useGetHubList();

  const params = useMemo(
    () => omitBy({ page, size: rowsPerPage, type }, (v) => v.toString().length < 1),
    [page, rowsPerPage, type]
  );
  const { data: partnerData } = stockApi.useGetStocksByPartner(
    { ...params, partnerId },
    {
      skip: !partnerId,
    }
  );

  const { data: hubData } = stockApi.useGetStocksByHub(
    { ...params, hubId },
    {
      skip: !hubId || partnerId,
    }
  );
  const data = useMemo(() => (partnerId ? partnerData : hubData), [hubData, partnerData, partnerId]);

  const handleDownload = async () => {
    if (buttonDisplay) {
      return;
    }
    setButtonDisplay(true);
    const response = await Axios.get(
      `/sales-admin/sales/admin/stock/${partnerId ? 'partners' : 'hubs'}/${partnerId || hubId}/download`,
      {
        params,
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        responseType: 'blob',
      }
    );
    if (response) {
      FileDownload(response.data, 'file.xls');
    }
    setButtonDisplay(false);
  };

  const handleChange = (value) => {
    setPartnerValue(value?.label ?? null);
    setPartnerId(value?.id ?? null);
    handleChangePage(null, 0);
  };

  const handleChangeHubId = (value) => {
    setPartnerId(null);
    setPartnerValue(null);
    setHubValue(value?.label ?? null);
    setHubId(value?.id ?? null);
    handleChangePage(null, 0);
  };

  const handleChangeType = (event) => {
    setType(event.target.value);
  };

  return (
    <Page title="User">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Stack sx={{ my: 2, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <FormControl value={type} onChange={handleChangeType}>
              <FormLabel id="demo-row-radio-buttons-group-label">Type</FormLabel>
              <RadioGroup row defaultValue="2">
                <FormControlLabel value="2" control={<Radio />} label="Start pack" />
                <FormControlLabel value="1" control={<Radio />} label="Iccid" />
              </RadioGroup>
            </FormControl>
            <Box display="flex" sx={{ mx: 2 }}>
              <Autocomplete
                value={partnerValue}
                onChange={(_, newValue) => {
                  handleChange(newValue);
                }}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                options={posManagerOrganizations || []}
                sx={{ width: 300 }}
                renderInput={(params) => <TextField {...params} label="Select Partner" />}
              />
            </Box>
            <Box display="flex" sx={{ mx: 2 }}>
              <Autocomplete
                value={hubValue}
                onChange={(_, newValue) => {
                  handleChangeHubId(newValue);
                }}
                inputValue={hubInputValue}
                onInputChange={(event, newInputValue) => {
                  setHubInputValue(newInputValue);
                }}
                options={hubList || []}
                sx={{ width: 300 }}
                renderInput={(params) => <TextField {...params} label="Select Hub" />}
              />
            </Box>
            <FormControl
              sx={{
                mx: 2,
                width: 130,
              }}
            >
              <InputLabel>Rows per page:</InputLabel>
              <Select value={rowsPerPage} label="Rows per page:" onChange={handleChangeRowsPerPage}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            <Button onClick={handleDownload} disabled={buttonDisplay} variant="contained">
              Download
            </Button>
          </Stack>
        </Stack>
        {data && (
          <Card>
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <UserListHead headLabel={TABLE_HEAD} rowCount={data?.data?.length} />
                  <TableBody>
                    {data?.data?.map((row, index) => {
                      const { identifier, partnerId, hubName, hubId, partnerName } = row;

                      return (
                        <TableRow hover key={index} tabIndex={-1}>
                          <TableCell component="th" scope="row" />
                          <TableCell align="left">{hubId}</TableCell>
                          <TableCell align="left">{hubName}</TableCell>
                          <TableCell align="left">{identifier}</TableCell>
                          <TableCell align="right">{partnerId}</TableCell>
                          <TableCell align="right">{partnerName}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          </Card>
        )}
        {data && (
          <Box marginTop={2}>
            <Pagination
              count={data?.metadata?.pagination?.total_page}
              color="primary"
              page={page + 1}
              onChange={(_, page) => handleChangePage(_, page - 1)}
            />
          </Box>
        )}
      </Container>
    </Page>
  );
}
